import {
  MutationFunctionOptions,
  MutationHookOptions,
  OperationVariables,
  useMutation,
} from '@apollo/client'
import { DocumentNode, ExecutionResult } from 'graphql'

import { MESSAGES } from '~/common/constants'
import {
  useFlashMessageContext,
  FlashMessage,
} from '~/views/contexts/FlashMessageContext'

export declare type ErrorHandlingMutationFunctionType<
  TData = any,
  TVariables = OperationVariables,
> = (
  variables: MutationFunctionOptions<TData, TVariables>,
  message?: FlashMessage,
) => Promise<ExecutionResult<TData> | Error>

const useErrorHandlingMutation = <TData = any, TVariables = OperationVariables>(
  mutation: DocumentNode,
  mutationOptions?: MutationHookOptions<TData, TVariables>,
  suppressFlashMessage?: boolean,
) => {
  const [mutationFn, result] = useMutation<TData, TVariables>(
    mutation,
    mutationOptions,
  )
  const { showFlashMessage } = useFlashMessageContext()

  const fireMutation = async (
    variables: MutationFunctionOptions<TData, TVariables>,
    message: FlashMessage = {
      message: MESSAGES.tryAgain,
      variant: 'error',
    },
  ): Promise<ExecutionResult<TData> | Error> => {
    try {
      return await mutationFn(variables)
    } catch (error) {
      if (!suppressFlashMessage) {
        showFlashMessage(message)
      }
      return error as Error
    }
  }

  return [fireMutation, result] as [typeof fireMutation, typeof result]
}

export default useErrorHandlingMutation
